import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import Table from '../../components/UI/Table';
import Dropdown from '../../components/UI/Dropdown';
import './Users.css';
import usersSearch from '../../assets/icons/devices-search.svg';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import DeviceDetailsModal from './DeviceDetailsModal';
import ConfirmationModal from '../../components/UI/ConfirmationModal';

const Users = () => {
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [pagesData, setPagesData] = useState({});
  const [pageCounts, setPageCounts] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedUserDevices, setSelectedUserDevices] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');

  const itemsPerPage = 10;

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to load users.');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const reloadData = async () => {
      try {
        await loadBlock(0, 1);
      } catch (error) {
        setErrorMessage('Failed to reload users.');
        setShowErrorModal(true);
      } finally {
        setIsReloading(false);
      }
    };
    if (isReloading) reloadData();
  }, [isReloading]);

  const loadBlock = async (blockIndex, desiredPage) => {
    if (pageCounts[blockIndex] && pagesData[desiredPage]) {
      setCurrentPage(desiredPage);
      return;
    }
    try {
      let resp;
      if (blockIndex === 0) {
        resp = await apiClient.get('/users');
      } else {
        const prevToken = tokens[blockIndex - 1];
        if (!prevToken) return;
        resp = await apiClient.get('/users', { params: { nextToken: prevToken } });
      }
      const nextToken = resp.data.nextToken || null;
      const raw = Array.isArray(resp.data) ? resp.data : resp.data.users;
      const usersWithDevices = raw.map(item => ({
        ...item.user,
        Devices: item.devices
      }));
      const pagesCountInBlock = Math.ceil(usersWithDevices.length / itemsPerPage);
      const newPagesData = { ...pagesData };
      for (let i = 0; i < pagesCountInBlock; i++) {
        const pageNumber = blockIndex * itemsPerPage + i + 1;
        const startIdx = i * itemsPerPage;
        const slice = usersWithDevices.slice(startIdx, startIdx + itemsPerPage);
        newPagesData[pageNumber] = slice;
      }
      const newPageCounts = [...pageCounts];
      newPageCounts[blockIndex] = pagesCountInBlock;
      const newTokens = [...tokens];
      newTokens[blockIndex] = nextToken;
      setPagesData(newPagesData);
      setPageCounts(newPageCounts);
      setTokens(newTokens);
      setCurrentPage(desiredPage);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const handlePageChange = (page) => {
    const blockIndex = Math.floor((page - 1) / itemsPerPage);
    if (pageCounts[blockIndex]) {
      setCurrentPage(page);
    } else {
      loadBlock(blockIndex, page);
    }
  };

  const handleNextBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / itemsPerPage);
    const nextBlock = currentBlock + 1;
    if (pageCounts[nextBlock]) {
      setCurrentPage(nextBlock * itemsPerPage + 1);
    } else {
      loadBlock(nextBlock, nextBlock * itemsPerPage + 1);
    }
  };

  const handlePrevBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / itemsPerPage);
    const prevBlock = currentBlock - 1;
    setCurrentPage(prevBlock * itemsPerPage + 1);
  };

  const handleDropdownToggle = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const handleExportUsers = async () => {
    try {
      const response = await apiClient.get('/export_users', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setErrorMessage('Failed to export users.');
      setShowErrorModal(true);
    }
  };

  const handleAddUserClick = () => setShowAddModal(true);
  const handleCloseAddUser = () => setShowAddModal(false);

  const handleAddUser = async (name, phone, status) => {
    try {
      const newUserObj = {
        Name: name,
        Phone: phone,
        UserName: phone,
        Roles: 'owner',
        Status: status
      };
      await apiClient.post('/users', newUserObj);
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
      setShowAddModal(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error adding user.');
      setShowErrorModal(true);
    }
  };

  const handleEditClick = (u) => {
    setEditUser(u);
    setShowEditModal(true);
  };

  const handleCloseEditUser = () => {
    setShowEditModal(false);
    setEditUser(null);
  };

  const handleSaveUser = async (originalUser, newName, newPhone, status) => {
    try {
      const update = {
        UserName: newPhone,
        Name: newName,
        Phone: newPhone,
        UserId: originalUser.UserId,
        Roles: originalUser.Roles,
        Status: status
      };
      await apiClient.put(`/users/${originalUser.UserId}`, update);
      const updatedPage = pagesData[currentPage].map(u =>
        u.UserId === originalUser.UserId ? { ...u, Name: newName, Phone: newPhone, Status: status } : u
      );
      setPagesData({
        ...pagesData,
        [currentPage]: updatedPage
      });
      setShowEditModal(false);
      setEditUser(null);
    } catch (error) {
      setErrorMessage('Error saving user.');
      setShowErrorModal(true);
    }
  };

  const handleRemoveClick = (user) => {
    setUserToRemove(user);
    setShowRemoveConfirm(true);
  };

  const handleSearchSubmit = async () => {
    if (!searchTerm.trim()) return;

    try {
      const encodedUsername = encodeURIComponent(searchTerm.trim());

      const response = await apiClient.get('/users', {
        params: { username: encodedUsername }
      });

      const raw = Array.isArray(response.data) ? response.data : response.data.users || [];

      const usersWithDevices = raw.map(item => ({
        ...item.user,
        Devices: item.devices
      }));

      setSearchResults(usersWithDevices);
      setCurrentPage(1);
    } catch (error) {
      console.error('Search failed:', error);
      const backendMessage = error?.response?.data || '';

      if (backendMessage.startsWith('Users not found')) {
        setErrorMessage(backendMessage);
      } else {
        setErrorMessage('Failed to search users.');
      }

      setShowErrorModal(true);
    }
  };


  const handleConfirmRemove = async () => {
    if (!userToRemove) return;
    const userId = userToRemove.UserId;
    try {
      await apiClient.delete(`/users/${userId}`);
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
    } catch (err) {
      setErrorMessage('Error removing user.');
      setShowErrorModal(true);
    } finally {
      setShowRemoveConfirm(false);
      setUserToRemove(null);
    }
  };

  const handleDeviceCountClick = (user) => {
    const devices = user.Devices || [];
    setSelectedUserDevices(devices);
    setSelectedUserName(user.Name);
    setShowDeviceModal(true);
  };

  const handleCloseDeviceModal = () => {
    setShowDeviceModal(false);
    setSelectedUserDevices([]);
    setSelectedUserName('');
  };

  const columns = ['USER', 'DEVICES', 'STATUS', ''];

  const currentUsers = searchResults || pagesData[currentPage] || [];
  const filteredUsers = currentUsers.filter((u) => {
    if (!searchTerm.trim()) return true;
    const lower = searchTerm.toLowerCase();
    return (u.Name || '').toLowerCase().includes(lower) || (u.Phone || '').toLowerCase().includes(lower);
  });

  const tableRows = filteredUsers.map((u, i) => {
    const devices = u.Devices || [];
    const isLastRow = i === filteredUsers.length - 1;
    const statusRaw = (u.Status || 'inactive').toLowerCase();
    const formattedStatus = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);
    const userStatusBadge = (
      <span className={statusRaw === 'inactive' ? 'inactive-badge' : 'active-badge'}>
        {formattedStatus}
      </span>
    );
    return {
      USER: (
        <div>
          <div>{u.Name}</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>Phone Number: {u.Phone || '—'}</div>
        </div>
      ),
      DEVICES: (
        <span 
          className="device-count-link" 
          onClick={() => handleDeviceCountClick(u)}
          style={{ 
            color: devices.length > 0 ? '#007bff' : '#666', 
            cursor: devices.length > 0 ? 'pointer' : 'default',
            textDecoration: devices.length > 0 ? 'underline' : 'none'
          }}
        >
          {devices.length}
        </span>
      ),
      STATUS: userStatusBadge,
      '': (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div
            className="more-button"
            onClick={(e) => { e.stopPropagation(); handleDropdownToggle(i); }}
            aria-label="more options"
          >
            ⋮
            {openDropdownIndex === i && (
              <Dropdown
                items={[
                  { label: 'Edit', onClick: () => handleEditClick(u) },
                  { label: 'Remove', onClick: () => handleRemoveClick(u), style: { color: 'red' } },
                ]}
                onClose={() => setOpenDropdownIndex(null)}
                placement={isLastRow ? 'top-left' : 'bottom-right'}
              />
            )}
          </div>
        </div>
      ),
    };
  });

  const finalTableData = tableRows;

  const currentBlockIndex = Math.floor((currentPage - 1) / itemsPerPage);
  const startPage = currentBlockIndex * itemsPerPage + 1;
  const pagesInBlock = pageCounts[currentBlockIndex] || 0;

  return (
    <div className="users-page">
      <h1>Users</h1>
      <div className="users-action-bar">
        <div className="users-left">
          <div className="users-search">
            <img src={usersSearch} className="users-search-icon" alt="search" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="search-button" onClick={handleSearchSubmit}>Search</button>
            {searchResults && <button className="clear-button" onClick={() => setSearchResults(null)}>Clear</button>}
          </div>
        </div>
        <div className="users-right">
          <button className="add-button" onClick={handleAddUserClick}>+ Add</button>
          <button className="export-button" onClick={handleExportUsers}>Export</button>
        </div>
      </div>

      <Table
        columns={columns}
        data={finalTableData}
        isLoading={isLoading || isReloading}
        placeholder="No users yet"
      />

      {!searchResults && (
        <div className="pagination">
          {currentBlockIndex > 0 && (
            <button onClick={handlePrevBlock}>Prev</button>
          )}
          {Array.from({ length: pagesInBlock }, (_, i) => startPage + i).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={page === currentPage ? 'active' : ''}
            >
              {page}
            </button>
          ))}
          {tokens[currentBlockIndex] && (
            <button onClick={handleNextBlock}>Next</button>
          )}
        </div>
      )}

      {showAddModal && (
        <AddUserModal
          onClose={handleCloseAddUser}
          onAdd={handleAddUser}
        />
      )}

      {showEditModal && editUser && (
        <EditUserModal
          onClose={handleCloseEditUser}
          onSave={handleSaveUser}
          user={editUser}
        />
      )}

      {showRemoveConfirm && (
        <ConfirmationModal
          isOpen={showRemoveConfirm}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={handleConfirmRemove}
          message={`Are you sure you want to remove user \"${userToRemove?.Name}\"?`}
          confirmLabel="Remove"
          isDanger={true}
        />
      )}
      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          onCancel={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}

      {showDeviceModal && (
        <DeviceDetailsModal
          isOpen={showDeviceModal}
          onClose={handleCloseDeviceModal}
          devices={selectedUserDevices}
          userName={selectedUserName}
        />
      )}
    </div>
  );
};

export default Users;